import { ChangeEvent, ComponentProps } from 'react';

import { MultiSelectFilter } from '@zendesk/forethought-elements';
import { FieldOption } from '../expression-builder/ExpressionBuilder';
import { ContextVariableFilterItemValue } from './types';
import { EventChartProperty } from 'src/services/insight-cards/types';
import { ContextVariable } from 'src/types/actionBuilderApiTypes';
import { QueryExpressionTypes } from 'src/types/queryExpressionTypes';

type Option = ComponentProps<typeof MultiSelectFilter>['options'][number];

// Helpers - Others Filter
export const flattenOptions = (options: Option[]): Option[] => {
  return options.flatMap(option =>
    option.options ? flattenOptions(option.options) : option,
  );
};
export const handleCheckboxClick = ({
  e,
  expandedValues,
  item,
  onChange,
  setExpandedValues,
  setIsCheckboxClicked,
  value,
}: {
  e: ChangeEvent<HTMLInputElement>;
  expandedValues: string[];
  item: Option;
  onChange: (value: string[]) => void;
  setExpandedValues: (value: string[]) => void;
  setIsCheckboxClicked: (value: boolean) => void;
  value: string[];
}) => {
  e.stopPropagation();
  setIsCheckboxClicked(true);

  const hasOptions = Boolean(item.options);
  const itemOptionValues = flattenOptions(item.options ?? [])
    .filter(child => !child.options?.length)
    .map(child => child.value);
  const isChecked = hasOptions
    ? itemOptionValues.length > 0 &&
      itemOptionValues.every(option => value.includes(option))
    : value.includes(item.value);

  // Condition 1: If unchecked and not expanded, expand
  if (!isChecked && hasOptions && !expandedValues.includes(item.value)) {
    setExpandedValues([...expandedValues, item.value]);
  }

  if (hasOptions) {
    if (isChecked) {
      onChange(value.filter(v => !itemOptionValues.includes(v)));
    } else {
      onChange([...value, ...itemOptionValues.filter(v => !value.includes(v))]);
    }
  } else {
    if (isChecked) {
      onChange(value.filter(v => v !== item.value));
    } else {
      onChange([...value, item.value]);
    }
  }
};
export const handleMenuItemClick = ({
  expandedValues,
  isCheckboxClicked,
  item,
  onChange,
  setExpandedValues,
  setIsCheckboxClicked,
  value,
}: {
  expandedValues: string[];
  isCheckboxClicked: boolean;
  item: Option;
  onChange: (value: string[]) => void;
  setExpandedValues: (value: string[]) => void;
  setIsCheckboxClicked: (value: boolean) => void;
  value: string[];
}) => {
  if (!isCheckboxClicked) {
    if (item.options) {
      // Expand/collapse logic for items with options array
      if (expandedValues.includes(item.value)) {
        setExpandedValues(expandedValues.filter(v => v !== item.value));
      } else {
        setExpandedValues([...expandedValues, item.value]);
      }
    } else {
      // Toggle select/deselect for item with no children
      if (value.includes(item.value)) {
        onChange(value.filter(v => v !== item.value));
      } else {
        onChange([...value, item.value]);
      }
    }
  } else {
    // If checkbox is clicked and item has no children, deselect it
    if (!item.options) {
      onChange(value.filter(v => v !== item.value));
    }
  }

  setIsCheckboxClicked(false);
};
// Helpers - Context Variable Filter
export const addDefaultField = (
  fieldOptions: FieldOption[],
  expression: ContextVariableFilterItemValue['expression'],
) => {
  if (expression.field) {
    return expression;
  }
  const firstFieldOptionItem = fieldOptions[0];
  return {
    ...expression,
    field: firstFieldOptionItem.value,
  };
};
export const getListTypeFieldOptions = (
  field: string,
  fieldOptions: FieldOption[],
) => fieldOptions.find(option => option.value === field)?.fieldOptions || [];
export const getQueryExpressionFieldType = (
  field: string | undefined,
  fieldOptions: FieldOption[],
) => {
  return fieldOptions.find(data => data.value === field)
    ?.type as QueryExpressionTypes;
};
export const updateArrayItem = (
  array: ContextVariableFilterItemValue[],
  index: number,
  newItem: Partial<ContextVariableFilterItemValue['expression']>,
) => {
  return array.map((item, i) => {
    if (i === index) {
      return {
        ...item,
        expression: {
          ...item.expression,
          ...validatedValues(newItem),
        },
      };
    }
    return item;
  });
};
const validatedValues = (
  expression: Partial<ContextVariableFilterItemValue['expression']>,
) => {
  if (expression.operator !== 'empty') {
    return expression;
  }
  return {
    ...expression,
    values: [],
  };
};

export const listCVObjectDeserialize = (
  target: string,
): ContextVariableFilterItemValue[] => {
  return JSON.parse(target) as ContextVariableFilterItemValue[];
};
export const listEventObjectDeserialize = (
  target: string,
): EventChartProperty[] => {
  return JSON.parse(target) as EventChartProperty[];
};
export const listCVObjectSerialize = (
  state: ContextVariableFilterItemValue[],
) => JSON.stringify(state);
export const listEventObjectSerialize = (state: EventChartProperty[]) =>
  JSON.stringify(state);

export const filterSupportedContextVariables = (
  contextVariables: ContextVariable[],
) => {
  return contextVariables
    .map(cv => ({
      category: 'context_variable',
      fieldOptions: [],
      label: cv.context_variable_name,
      type: cv.context_variable_type as 'SHORT_TEXT' | 'LIST',
      value: cv.context_variable_id,
    }))
    .filter(
      cv =>
        cv.value !== 'article_ids' &&
        cv.value !== 'free_form_query' &&
        cv.value !== 'conversation_id' &&
        // Workflow tag value is UUID so filtering by label
        cv.label !== 'Workflow Tag',
    );
};

/**
 * "All Channels" is persisted as an empty selection so downstream consumers keep
 * their empty-means-all behavior. Collapse a full selection back to [] (compared
 * by content, not length, so stale state can't collapse a partial selection).
 */
export const collapseAllChannelsSelection = (
  selected: string[],
  allChannelValues: string[],
): string[] => {
  const selectedSet = new Set(selected);
  const isAllSelected =
    allChannelValues.length > 0 &&
    allChannelValues.every(value => selectedSet.has(value));
  return isAllSelected ? [] : selected;
};
